import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';

const SlugGeneratorTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [options, setOptions] = useState({
    lowercase: true,
    removeSpecial: true,
    separator: '-',
  });

  const generateSlug = () => {
    if (!input.trim()) {
      toast.error('Please enter text to convert');
      return;
    }

    let slug = input.trim();
    
    if (options.lowercase) {
      slug = slug.toLowerCase();
    }
    
    // Replace accented characters
    slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    if (options.removeSpecial) {
      slug = slug.replace(/[^a-zA-Z0-9\s-]/g, '');
    }
    
    // Replace spaces with separator
    slug = slug.replace(/\s+/g, options.separator);
    
    // Remove multiple consecutive separators
    slug = slug.replace(new RegExp(`${options.separator}+`, 'g'), options.separator);
    
    // Remove leading/trailing separators
    slug = slug.replace(new RegExp(`^${options.separator}|${options.separator}$`, 'g'), '');

    setOutput(slug);
    toast.success('Slug generated!');
  };

  const copyOutput = () => {
    if (output) {
      navigator.clipboard.writeText(output);
      toast.success('Copied!');
    }
  };

  return (
    <div className="space-y-4" data-testid="slug-generator-tool">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Slug Generator</h3>
      </div>

      <Input
        placeholder="Enter text to convert to slug..."
        value={input}
        onChange={(e) => setInput(e.target.value)}
        className="bg-background/50"
        data-testid="input"
      />

      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="lowercase"
            checked={options.lowercase}
            onCheckedChange={(checked) => setOptions({ ...options, lowercase: checked })}
          />
          <label htmlFor="lowercase" className="text-sm">Lowercase</label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="removeSpecial"
            checked={options.removeSpecial}
            onCheckedChange={(checked) => setOptions({ ...options, removeSpecial: checked })}
          />
          <label htmlFor="removeSpecial" className="text-sm">Remove special chars</label>
        </div>
      </div>

      <div className="flex gap-2">
        <label className="text-sm text-muted-foreground self-center">Separator:</label>
        {['-', '_', '.'].map((sep) => (
          <Button
            key={sep}
            variant={options.separator === sep ? 'default' : 'outline'}
            size="sm"
            onClick={() => setOptions({ ...options, separator: sep })}
          >
            {sep === '-' ? 'dash' : sep === '_' ? 'underscore' : 'dot'}
          </Button>
        ))}
      </div>

      <Button onClick={generateSlug} className="w-full" data-testid="generate-btn">
        Generate Slug
      </Button>

      {output && (
        <div className="flex gap-2">
          <Input
            value={output}
            readOnly
            className="font-mono bg-background/50"
            data-testid="output"
          />
          <Button variant="outline" onClick={copyOutput}>
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default SlugGeneratorTool;
